import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsHome.css';

function formatCount(value) {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value || 0).toLocaleString('en-US');
}

function formatMoney(cents) {
  if (cents === null || cents === undefined || cents === '') return '—';
  const n = Number(cents || 0);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n / 100);
}

function formatPercent(value) {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return `${numeric.toFixed(1)}%`;
}

function formatHours(value) {
  if (value === null || value === undefined || value === '') return '—';
  const hours = Number(value || 0);
  if (!Number.isFinite(hours)) return '—';
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

function formatLiftPercent(value) {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  const prefix = numeric > 0 ? '+' : '';
  return `${prefix}${numeric.toFixed(1)}%`;
}

function formatRiskState(value) {
  if (!value) return 'Normal';
  return String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function MetricCard({ title, value, caption }) {
  return (
    <article className="ops-card">
      <div className="ops-card-title">{title}</div>
      <div className="ops-card-value">{value}</div>
      <div className="ops-card-caption">{caption}</div>
    </article>
  );
}

function TrendLine({ title, subtitle, points, valueKey = 'ratePercent' }) {
  const safePoints = Array.isArray(points) ? points : [];
  const values = safePoints
    .map((point) => Number(point?.[valueKey]))
    .filter((value) => Number.isFinite(value));

  if (!safePoints.length || !values.length) {
    return (
      <article className="ops-card">
        <div className="ops-card-title">{title}</div>
        <div className="ops-card-caption">{subtitle}</div>
        <div className="ops-card-caption" style={{ marginTop: 12 }}>No trend data in this window.</div>
      </article>
    );
  }

  const width = 320;
  const height = 110;
  const padding = 16;
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue || 1;
  const stepX = safePoints.length > 1 ? (width - padding * 2) / (safePoints.length - 1) : 0;
  const polyline = safePoints.map((point, index) => {
    const value = Number(point?.[valueKey] || 0);
    const x = padding + (stepX * index);
    const y = height - padding - (((value - minValue) / range) * (height - padding * 2));
    return `${x},${y}`;
  }).join(' ');

  return (
    <article className="ops-card">
      <div className="ops-card-title">{title}</div>
      <div className="ops-card-caption">{subtitle}</div>
      <div style={{ marginTop: 12 }}>
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="110" role="img" aria-label={title}>
          <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="rgba(22,61,74,0.16)" strokeWidth="1" />
          <polyline
            fill="none"
            stroke="#1f6f78"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={polyline}
          />
          {safePoints.map((point, index) => {
            const value = Number(point?.[valueKey] || 0);
            const x = padding + (stepX * index);
            const y = height - padding - (((value - minValue) / range) * (height - padding * 2));
            return <circle key={`${point?.label || index}_${index}`} cx={x} cy={y} r="3.5" fill="#1f6f78" />;
          })}
        </svg>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8, fontSize: 12, color: 'var(--ops-muted, #5d7780)' }}>
        <span>{safePoints[0]?.label || 'Start'}</span>
        <span>{safePoints[safePoints.length - 1]?.label || 'End'}</span>
      </div>
    </article>
  );
}

const DEFAULT_FILTERS = {
  window: '30d',
  market: 'all',
  state: 'all',
  category: '',
  customStart: '',
  customEnd: '',
};

export default function OpsBusinessMetrics() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  async function loadMetrics(activeFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('window', activeFilters.window || '30d');
      params.set('market', activeFilters.market || 'all');
      params.set('state', activeFilters.state || 'all');
      if (activeFilters.category) params.set('category', activeFilters.category);
      if (activeFilters.window === 'custom') {
        if (activeFilters.customStart) params.set('start', activeFilters.customStart);
        if (activeFilters.customEnd) params.set('end', activeFilters.customEnd);
      }
      const resp = await apiFetch(`/ops/program-health/business-metrics?${params.toString()}`);
      setData(resp || null);
    } catch (err) {
      const status = err?.status;
      if (status === 401 || status === 403) {
        clearAuthToken();
        navigate('/login', { replace: true });
        return;
      }
      setData(null);
      setError(err?.data?.error || err?.message || 'Failed to load business metrics');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (filters.window === 'custom' && (!filters.customStart || !filters.customEnd)) {
      return;
    }
    if (filters.window === 'custom' && filters.customStart && filters.customEnd) {
      const start = new Date(filters.customStart);
      const end = new Date(filters.customEnd);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start > end) {
        setLoading(false);
        setError('Custom date range is invalid. Start must be on or before End.');
        return;
      }
    }
    loadMetrics(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const metrics = data?.metrics || {};
  const participation = metrics.participation || {};
  const timeToFirstAttributed = metrics.timeToFirstAttributed || {};
  const wallet = metrics.wallet || {};
  const fundingLiabilities = metrics.fundingLiabilities || {};
  const lifts = metrics.lifts || {};

  const participationCards = [
    {
      title: 'Business Participation Rate (30d)',
      value: formatPercent(participation.ratePercent),
      caption: `${formatCount(participation.activeBusinesses30d)} active of ${formatCount(participation.onboardedBusinesses)} onboarded businesses`,
    },
  ];

  const ttfatCards = [
    {
      title: 'Median Time To First Attributed Eligible Transaction',
      value: formatHours(timeToFirstAttributed.medianHours),
      caption: `${formatCount(timeToFirstAttributed.sampleSize)} businesses contributed to the current sample`,
    },
  ];

  const walletCards = [
    {
      title: 'Wallet Uptime',
      value: formatPercent(wallet.uptimePercent),
      caption: 'Average funded live-days across onboarded businesses in the current slice',
    },
    {
      title: 'Insufficient-Funds Inactivations',
      value: formatCount(wallet.insufficientFundsInactivations),
      caption: 'Account-status events caused by nonpayment or funding failure in the selected window',
    },
  ];

  const fundingLiabilityCards = [
    {
      title: 'Pending Consumer Spend',
      value: formatMoney(fundingLiabilities.pendingConsumerSpendCents),
      caption: `${formatCount(fundingLiabilities.pendingTransactionCount)} matched pending transaction${Number(fundingLiabilities.pendingTransactionCount || 0) === 1 ? '' : 's'}; informational only`,
    },
    {
      title: 'Pending Exposure',
      value: formatMoney(fundingLiabilities.pendingExposureCents),
      caption: 'Estimated contribution plus fee exposure if current matched pending transactions post',
    },
    {
      title: 'Actual Wallet Balance',
      value: formatMoney(fundingLiabilities.actualWalletBalanceCents),
      caption: 'Settled wallet balance from posted ledger activity only',
    },
    {
      title: 'Projected Available Balance',
      value: formatMoney(fundingLiabilities.projectedAvailableBalanceCents),
      caption: fundingLiabilities.exposureRatioPercent == null
        ? `Risk: ${formatRiskState(fundingLiabilities.riskState)}`
        : `Exposure ratio: ${formatPercent(fundingLiabilities.exposureRatioPercent)} • Risk: ${formatRiskState(fundingLiabilities.riskState)}`,
    },
  ];

  const liftCards = [
    {
      title: 'Visit Frequency Lift',
      value: formatLiftPercent(lifts.visitFrequencyLiftPercent),
      caption: 'Post-vs-baseline lift. Beta placeholder until a stable pre-live baseline is tracked.',
    },
    {
      title: 'Average Ticket Lift',
      value: formatLiftPercent(lifts.averageTicketLiftPercent),
      caption: 'Post-vs-baseline ticket-size lift. Beta placeholder until a stable baseline is tracked.',
    },
  ];

  return (
    <OpsLayout
      title="Business Metrics"
      subtitle="Platform-level business participation, ramp, wallet reliability, and beta lift metrics"
    >
      <div className="ops-nfp-content">
        <section className="ops-nfp-panel" style={{ marginTop: 0 }}>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Market</span>
              <select
                value={filters.market}
                onChange={(e) => {
                  setError('');
                  setFilters((prev) => ({ ...prev, market: e.target.value }));
                }}
              >
                {(Array.isArray(data?.filters?.markets) ? data.filters.markets : [{ key: 'all', label: 'All Markets' }]).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>State</span>
              <select
                value={filters.state}
                onChange={(e) => {
                  setError('');
                  setFilters((prev) => ({ ...prev, state: e.target.value, market: 'all' }));
                }}
              >
                {(Array.isArray(data?.filters?.states) ? data.filters.states : [{ key: 'all', label: 'All States' }]).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Category</span>
              <select
                value={filters.category}
                onChange={(e) => {
                  setError('');
                  setFilters((prev) => ({ ...prev, category: e.target.value }));
                }}
              >
                <option value="">All Categories</option>
                {(Array.isArray(data?.filters?.categories) ? data.filters.categories : []).map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label className="ops-nfp-field">
              <span style={{ fontWeight: 600 }}>Date Range</span>
              <select
                value={filters.window}
                onChange={(e) => {
                  const nextWindow = e.target.value;
                  setFilters((prev) => ({
                    ...prev,
                    window: nextWindow,
                    customStart: nextWindow === 'custom' ? prev.customStart : '',
                    customEnd: nextWindow === 'custom' ? prev.customEnd : '',
                  }));
                }}
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="custom">Custom</option>
              </select>
            </label>

            {filters.window === 'custom' ? (
              <>
                <label className="ops-nfp-field">
                  <span style={{ fontWeight: 600 }}>Start</span>
                  <input
                    type="date"
                    value={filters.customStart}
                    onChange={(e) => {
                      setError('');
                      setFilters((prev) => ({ ...prev, customStart: e.target.value }));
                    }}
                  />
                </label>
                <label className="ops-nfp-field">
                  <span style={{ fontWeight: 600 }}>End</span>
                  <input
                    type="date"
                    value={filters.customEnd}
                    onChange={(e) => {
                      setError('');
                      setFilters((prev) => ({ ...prev, customEnd: e.target.value }));
                    }}
                  />
                </label>
              </>
            ) : null}
          </div>

          {Array.isArray(data?.notes) && data.notes.length ? (
            <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
              {data.notes.map((note) => (
                <div key={note} style={{ fontSize: 12, color: 'var(--ops-muted, #5d7780)' }}>{note}</div>
              ))}
            </div>
          ) : null}
        </section>

        {error ? (
          <section className="ops-nfp-panel">
            <div className="ops-empty-state" style={{ color: '#b74d18' }}>{error}</div>
          </section>
        ) : null}

        {loading ? (
          <section className="ops-nfp-panel">
            <div className="ops-empty-state">Loading business metrics…</div>
          </section>
        ) : (
          <>
            <section className="ops-panel">
              <h2>Business Participation</h2>
              <div className="ops-cards">
                {participationCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Trend Lines</h2>
              <div className="ops-cards">
                <TrendLine
                  title="Participation Rate Trend"
                  subtitle="Rolling 30-day active business participation across the selected window"
                  points={participation.trend}
                />
                <TrendLine
                  title="Wallet Uptime Trend"
                  subtitle="Average wallet-funded uptime across the selected cohort"
                  points={wallet.trend}
                />
              </div>
            </section>

            <section className="ops-panel">
              <h2>Time To First Attributed Value</h2>
              <div className="ops-cards">
                {ttfatCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Wallet Reliability</h2>
              <div className="ops-cards">
                {walletCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Funding &amp; Liabilities</h2>
              <p className="ops-action-queue-subtitle" style={{ marginTop: -4 }}>
                Applies to matched pending transactions only. Pending exposure does not debit the wallet ledger.
              </p>
              <div className="ops-cards">
                {fundingLiabilityCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>

            <section className="ops-panel">
              <h2>Lift Metrics</h2>
              <div className="ops-cards">
                {liftCards.map((card) => <MetricCard key={card.title} {...card} />)}
              </div>
            </section>
          </>
        )}
      </div>
    </OpsLayout>
  );
}
